// KHO VIDEO (06/10/2026) — phần THUẦN dùng chung Boss (máy chủ + trang web) và plugin.
//
// Kho video của MỘT chủ (Boss) = video TẢI LÊN ở trang "Kho video" (bảng kho_video, mã "kv-…"; tệp nằm NGOÀI boss.db, trong thư viện mục kho-video/ dưới thư mục dữ liệu).
// Mỗi video có siêu dữ liệu (tiêu đề, mô tả, hashtag, nhãn) + thông số đọc phía trình duyệt lúc tải lên (thời lượng, rộng × cao).
// Bước "Đăng video" của kịch bản lấy video kế tiếp của KHO của chủ bot (src/shared/video-chon.ts) và đăng lên Facebook / YouTube / TikTok.
//
// Mọi hàm ở đây tất định, không đụng mạng / đĩa → unit test được.

export const VIDEO_MIME = ["video/mp4", "video/quicktime", "video/webm"] as const;
export type VideoMime = (typeof VIDEO_MIME)[number];
export const VIDEO_DUOI = [".mp4", ".mov", ".m4v", ".webm"] as const;
export const ID_VIDEO = /^kv-[a-z0-9]{14}$/;

/** Kích thước một tệp video tối đa (byte) — máy chủ đọc từ cấu hình khoVideo.byteToiDa, mặc định này. */
export const KV_BYTE_TOI_DA_MAC_DINH = 500 * 1024 * 1024;
/** Tổng dung lượng video của MỘT chủ (byte) mặc định. */
export const KV_HAN_MUC_CHU_MAC_DINH = 5 * 1024 * 1024 * 1024;
/** Số video tối đa của MỘT chủ mặc định. */
export const KV_SO_TOI_DA_MAC_DINH = 300;
/** Chừa lại trên đĩa (byte) — còn ít hơn (tệp + chừa) thì từ chối tải lên, không làm đầy đĩa của máy chủ. */
export const KV_CHUA_DIA = 1024 * 1024 * 1024;
/** Cỡ một khúc plugin tải về (byte) — plugin xin từng khúc bằng HTTP Range. */
export const KV_KHUC_BYTE = 6 * 1024 * 1024;

export const TIEU_DE_TOI_DA = 100; // khớp YouTube (tiêu đề ≤ 100 ký tự)
export const MO_TA_TOI_DA = 4000; // YouTube ≤ 5000, TikTok ≤ 4000 — lấy mức chung nhỏ hơn
export const HASHTAG_TOI_DA = 30;
export const HASHTAG_DO_DAI_TOI_DA = 40;
export const TEN_TEP_TOI_DA = 120;
export const THOI_LUONG_TOI_DA_GIAY = 12 * 3600;
export const CANH_TOI_DA = 16384;

export interface NhanDienVideo { mime: VideoMime; duoi: ".mp4" | ".mov" | ".webm" }

/**
 * Nhận ra loại video THẬT từ các byte đầu (không tin đuôi tệp / Content-Type): MP4/M4V/MOV có "ftyp" ở byte 4–8 (brand "qt  " → QuickTime .mov),
 * WebM/Matroska bắt đầu bằng EBML 1A 45 DF A3. Không nhận ra → null. Cần ít nhất 12 byte.
 */
export function nhanDienVideo(dau: Uint8Array): NhanDienVideo | null {
  if (!dau || dau.length < 12) return null;
  if (dau[0] === 0x1a && dau[1] === 0x45 && dau[2] === 0xdf && dau[3] === 0xa3) return { mime: "video/webm", duoi: ".webm" };
  if (dau[4] === 0x66 && dau[5] === 0x74 && dau[6] === 0x79 && dau[7] === 0x70) {
    const brand = String.fromCharCode(dau[8]!, dau[9]!, dau[10]!, dau[11]!);
    if (brand === "qt  ") return { mime: "video/quicktime", duoi: ".mov" };
    // Chỉ nhận các brand video thường gặp; brand lạ (heic, avif…) cũng dùng khung ftyp nhưng không phải video.
    if (/^(isom|iso\d|mp4\d|mp41|mp42|avc1|M4V |M4VH|M4VP|dash|MSNV|3gp\d|f4v |XAVC|mmp4|msdh)/.test(brand) || /^(mp4|iso|avc|M4V|3gp|dash|MSNV)/.test(brand)) return { mime: "video/mp4", duoi: ".mp4" };
    return null;
  }
  return null;
}

/** Đuôi tệp có phải đuôi video nhận không (so không phân biệt hoa thường). */
export function duoiVideoHopLe(ten: unknown): boolean {
  if (typeof ten !== "string") return false;
  const m = /(\.[A-Za-z0-9]{1,6})$/.exec(ten.trim());
  return !!m && (VIDEO_DUOI as readonly string[]).includes(m[1]!.toLowerCase());
}

/** Tên tệp hiện cho người: một dòng, bỏ ký tự điều khiển + dấu phân cách đường dẫn, ≤ TEN_TEP_TOI_DA ký tự; rỗng → "video". Tiếng Việt / emoji giữ nguyên. */
export function chuanTenTepVideo(v: unknown): string {
  const s = typeof v === "string" ? v.replace(/[\u0000-\u001f\u007f\\/]+/g, " ").replace(/\s+/g, " ").trim() : "";
  return Array.from(s).slice(0, TEN_TEP_TOI_DA).join("").trim() || "video";
}

/** Tên tệp bỏ đuôi — làm tiêu đề mặc định khi người dùng chưa đặt. */
export function tieuDeTuTenTep(ten: unknown): string {
  const s = chuanTenTepVideo(ten).replace(/\.[A-Za-z0-9]{1,6}$/, "").replace(/[_]+/g, " ").trim();
  return chuanTieuDe(s);
}

/** Tiêu đề: một dòng, ≤ TIEU_DE_TOI_DA ký tự (đếm theo KÝ TỰ, không tách đôi emoji). `<` `>` bị bỏ (YouTube không nhận). */
export function chuanTieuDe(v: unknown): string {
  if (typeof v !== "string") return "";
  const s = v.replace(/[\u0000-\u001f\u007f]+/g, " ").replace(/[<>]/g, "").replace(/\s+/g, " ").trim();
  return Array.from(s).slice(0, TIEU_DE_TOI_DA).join("").trim();
}

/** Mô tả: nhiều dòng được, bỏ ký tự điều khiển (trừ xuống dòng), gộp tối đa 2 dòng trống liền, ≤ MO_TA_TOI_DA ký tự. `<` `>` bỏ. */
export function chuanMoTa(v: unknown): string {
  if (typeof v !== "string") return "";
  const s = v.replace(/\r\n?/g, "\n").replace(/[\u0000-\u0009\u000b-\u001f\u007f]+/g, " ").replace(/[<>]/g, "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return Array.from(s).slice(0, MO_TA_TOI_DA).join("").trim();
}

/**
 * Hashtag: nhận mảng hoặc chữ ("#áo #hè, mẹo vặt") → mỗi mục bỏ "#", chỉ giữ chữ cái (có dấu) / số / gạch dưới, khoảng trắng bên trong bị bỏ, không trùng
 * (không phân biệt hoa thường, giữ cách viết đầu tiên), mỗi mục ≤ HASHTAG_DO_DAI_TOI_DA, tối đa HASHTAG_TOI_DA mục.
 */
export function chuanHashtag(v: unknown): string[] {
  const dau = Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : typeof v === "string" ? [v] : [];
  const thanh: string[] = [];
  for (const d of dau) for (const phan of d.split(/[,;\n#]+|\s{2,}/)) thanh.push(phan);
  // "#a #b" tách theo "#"; chữ không có "#" nhưng cách nhau bằng khoảng trắng đơn ("áo hè") là NHIỀU hashtag.
  const ra: string[] = [];
  const thay = new Set<string>();
  for (const p of thanh.flatMap((x) => x.split(/\s+/))) {
    const s = Array.from(p.replace(/[^\p{L}\p{N}_]+/gu, "")).slice(0, HASHTAG_DO_DAI_TOI_DA).join("");
    if (!s) continue;
    const k = s.toLocaleLowerCase("vi");
    if (thay.has(k)) continue;
    thay.add(k);
    ra.push(s);
    if (ra.length >= HASHTAG_TOI_DA) break;
  }
  return ra;
}

export const ghiHashtag = (ds: readonly string[]): string => ds.map((x) => `#${x}`).join(" ");

export interface MetaVideo { thoiLuong: number | null; rong: number | null; cao: number | null }

const soHopLe = (v: unknown, toiDa: number, nguyen: boolean): number | null => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : NaN;
  if (!Number.isFinite(n) || n <= 0 || n > toiDa) return null;
  return nguyen ? Math.round(n) : Math.round(n * 1000) / 1000;
};
/** Thông số đọc ở trình duyệt (không tin): số hữu hạn, > 0, trong ngưỡng; ngoài ra → null (= "chưa biết"). */
export function chuanMeta(v: { thoiLuong?: unknown; rong?: unknown; cao?: unknown } | null | undefined): MetaVideo {
  return { thoiLuong: soHopLe(v?.thoiLuong, THOI_LUONG_TOI_DA_GIAY, false), rong: soHopLe(v?.rong, CANH_TOI_DA, true), cao: soHopLe(v?.cao, CANH_TOI_DA, true) };
}

export type TiLeKhung = "doc" | "ngang" | "vuong";
/** Dọc 9:16 (rộng/cao ≤ 0,65) / vuông (0,65 < r ≤ 1,15) / ngang. Thiếu số đo → null. */
export function tiLeKhung(m: Pick<MetaVideo, "rong" | "cao">): TiLeKhung | null {
  if (!m.rong || !m.cao) return null;
  const r = m.rong / m.cao;
  return r <= 0.65 ? "doc" : r <= 1.15 ? "vuong" : "ngang";
}

export const REEL_TOI_DA_GIAY = 90;
export const SHORTS_TOI_DA_GIAY = 60;
export const TIKTOK_TOI_DA_GIAY = 10 * 60;
export const TIKTOK_TOI_DA_BYTE = 500 * 1024 * 1024;

export type NenTangVideo = "facebook" | "youtube" | "tiktok";
export const NEN_TANG_VIDEO: readonly NenTangVideo[] = ["facebook", "youtube", "tiktok"];
export const TEN_NEN_TANG_VIDEO: Readonly<Record<NenTangVideo, string>> = { facebook: "Facebook", youtube: "YouTube", tiktok: "TikTok" };
export const laNenTangVideo = (x: unknown): x is NenTangVideo => x === "facebook" || x === "youtube" || x === "tiktok";

export type KieuDangYeuCau = "tu_nhan" | "bai_thuong" | "reel";
export type KieuDangVideo = "reel" | "bai_tuong" | "shorts" | "video_thuong" | "tiktok";
export const NHAN_KIEU_DANG: Readonly<Record<KieuDangVideo, string>> = { reel: "Reel", bai_tuong: "Bài video trên Tường", shorts: "Shorts", video_thuong: "Video thường", tiktok: "Video TikTok" };

export interface KqPhanLoai { ok: boolean; kieu: KieuDangVideo; lyDo: string }

/**
 * Chọn KIỂU ĐĂNG theo nền tảng + thời lượng + tỉ lệ (+ ép kiểu của người soạn).
 *  Facebook: ≤ 90 giây VÀ dọc 9:16 → Reel; còn lại → bài video trên Tường. "reel" (ép) mà không đủ điều kiện → ok=false (không đăng sai kiểu).
 *  YouTube: ≤ 60 giây VÀ dọc → Shorts; còn lại → video thường. (YouTube tự nhận Shorts khi ≤ 60 giây và dọc/vuông — không ép được; ghi lý do.)
 *  TikTok: một kiểu; kiểm giới hạn (thời lượng ≤ 10 phút, tệp ≤ 500 MB) — vượt → ok=false.
 * Thiếu thời lượng/tỉ lệ (0, âm, null) → KHÔNG đoán là ngắn/dọc: Facebook/YouTube coi là bài/video thường (ghi rõ); ép "reel" thì từ chối.
 */
export function phanLoaiKieuDang(nenTang: NenTangVideo, m: MetaVideo, yeuCau: KieuDangYeuCau = "tu_nhan", kichThuoc?: number): KqPhanLoai {
  const giay = m.thoiLuong;
  const kl = tiLeKhung(m);
  const coSo = giay !== null && giay > 0 && kl !== null;
  const doc = kl === "doc";
  if (nenTang === "tiktok") {
    if (giay !== null && giay > TIKTOK_TOI_DA_GIAY) return { ok: false, kieu: "tiktok", lyDo: `video dài ${Math.round(giay)} giây — TikTok (đăng từ web) nhận tối đa ${TIKTOK_TOI_DA_GIAY / 60} phút` };
    if (typeof kichThuoc === "number" && kichThuoc > TIKTOK_TOI_DA_BYTE) return { ok: false, kieu: "tiktok", lyDo: `tệp ${(kichThuoc / 1048576).toFixed(0)} MB — quá giới hạn đăng TikTok của hệ thống (${TIKTOK_TOI_DA_BYTE / 1048576} MB)` };
    return { ok: true, kieu: "tiktok", lyDo: giay === null ? "chưa biết thời lượng — để TikTok tự kiểm khi tải lên" : "đăng video lên tài khoản TikTok" };
  }
  if (nenTang === "facebook") {
    if (yeuCau === "bai_thuong") return { ok: true, kieu: "bai_tuong", lyDo: "kịch bản đặt: luôn đăng bài video trên Tường" };
    const dat = coSo && giay! <= REEL_TOI_DA_GIAY && doc;
    if (yeuCau === "reel") return dat ? { ok: true, kieu: "reel", lyDo: "kịch bản đặt: luôn Reel — video đủ điều kiện" } : { ok: false, kieu: "reel", lyDo: `kịch bản đặt "luôn Reel" nhưng video ${coSo ? `${Math.round(giay!)} giây, ${kl === "doc" ? "dọc" : kl === "vuong" ? "vuông" : "ngang"}` : "chưa biết thời lượng / tỉ lệ"} — Reel cần ≤ ${REEL_TOI_DA_GIAY} giây và khung dọc 9:16` };
    if (dat) return { ok: true, kieu: "reel", lyDo: `video ngắn (${Math.round(giay!)} giây ≤ ${REEL_TOI_DA_GIAY}) và dọc 9:16 → đăng dạng Reel` };
    return { ok: true, kieu: "bai_tuong", lyDo: coSo ? `video ${Math.round(giay!)} giây, khung ${kl === "doc" ? "dọc" : kl === "vuong" ? "vuông" : "ngang"} — không đủ điều kiện Reel → bài video trên Tường` : "chưa biết thời lượng / tỉ lệ khung hình → đăng bài video trên Tường (không đoán Reel)" };
  }
  // youtube
  const shorts = coSo && giay! <= SHORTS_TOI_DA_GIAY && (doc || kl === "vuong");
  if (shorts) return { ok: true, kieu: "shorts", lyDo: `video ngắn (${Math.round(giay!)} giây ≤ ${SHORTS_TOI_DA_GIAY}) và ${doc ? "dọc" : "vuông"} → YouTube tự nhận là Shorts${yeuCau === "bai_thuong" ? " (kịch bản đặt \"luôn video thường\" nhưng YouTube không cho ép: video này vẫn thành Shorts)" : ""}` };
  return { ok: true, kieu: "video_thuong", lyDo: coSo ? `video ${Math.round(giay!)} giây, khung ${kl === "doc" ? "dọc" : kl === "vuong" ? "vuông" : "ngang"} → video thường` : "chưa biết thời lượng / tỉ lệ khung hình → video thường (không đoán Shorts)" };
}

/** Lý do video KHÔNG hợp nền tảng (loại khỏi hàng đợi); null = hợp. Dùng lúc cấp phát. */
export function lyDoKhongHop(nenTang: NenTangVideo, m: MetaVideo, kichThuoc: number, yeuCau: KieuDangYeuCau = "tu_nhan"): string | null {
  const k = phanLoaiKieuDang(nenTang, m, yeuCau, kichThuoc);
  return k.ok ? null : k.lyDo;
}

/** Hạn mức dung lượng: trả lý do từ chối (câu tiếng Việt) hoặc null. */
export function loiHanMucTaiLen(o: { kichThuoc: number; byteToiDa: number; daDung: number; hanMucChu: number; soVideo: number; soToiDa: number }): string | null {
  const mb = (b: number) => `${(b / 1048576).toFixed(b >= 1048576 * 10 ? 0 : 1)} MB`;
  if (!Number.isFinite(o.kichThuoc) || o.kichThuoc <= 0) return "tệp rỗng (0 byte) — chọn tệp video khác";
  if (o.kichThuoc > o.byteToiDa) return `tệp quá lớn (${mb(o.kichThuoc)}) — tối đa ${mb(o.byteToiDa)} mỗi video`;
  if (o.soVideo >= o.soToiDa) return `kho đã đủ ${o.soToiDa} video — xoá bớt video không dùng`;
  if (o.daDung + o.kichThuoc > o.hanMucChu) return `kho video của bạn sắp đầy (${mb(o.daDung)}/${mb(o.hanMucChu)}) — xoá bớt video không dùng rồi tải lại`;
  return null;
}

/** "1:23" / "12:05" / "1:02:03" — thời lượng hiện cho người. null → "chưa rõ". */
export function chuThoiLuong(giay: number | null | undefined): string {
  if (typeof giay !== "number" || !Number.isFinite(giay) || giay < 0) return "chưa rõ";
  const t = Math.round(giay);
  const h = Math.floor(t / 3600), p = Math.floor((t % 3600) / 60), s = t % 60;
  const hai = (n: number) => String(n).padStart(2, "0");
  return h ? `${h}:${hai(p)}:${hai(s)}` : `${p}:${hai(s)}`;
}

/** Hai mô tả "giống 100%": so sau khi bỏ khoảng trắng thừa + hoa thường. Cả hai rỗng → false (chưa có gì để so). */
export function moTaGiongHet(a: unknown, b: unknown): boolean {
  const chuan = (x: unknown) => (typeof x === "string" ? x.replace(/\s+/g, " ").trim().toLocaleLowerCase("vi") : "");
  const x = chuan(a), y = chuan(b);
  return !!x && x === y;
}
